import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addToJobQueue, removeFromJobQueue } from "slices/app/appSlice";
import {
  selectDocumentParagraphs,
  selectParagraphRows,
  selectTotalPages,
} from "slices/documentViewer/selectors";
import { getFileExtension } from "utils/getFileFormat";

import {
  DOX_ID_PREFIX,
  PAGE_ID_PREFIX,
  RENDER_POLL_INTERVAL_MS,
  RENDER_WAIT_TIMEOUT_MS,
  TEXT_LAYER_GRACE_MS,
} from "../constants";
import findSequentialNodes from "../utils/findSequentialNodes";
import {
  highlightTextRange,
  isHighlightWrapper,
  locateTextRange,
  unwrapHighlight,
} from "../utils/highlightTextRange";
import resolveDocumentAnchor from "../utils/resolveDocumentAnchor";
import classes from "./useDocumentViewer.module.scss";

const NODE_SCROLL_TO = { behavior: "auto", block: "center" };

// The shredding backend labels a PDF location "Page N", so the page is the first digit
// group in the string, not every digit in it.
export const locationToPageNumber = (loc = "") => {
  const [firstDigitGroup] = String(loc).match(/\d+/) || [];

  return parseInt(firstDigitGroup || "1", 10);
};

// A PDF renders (and can therefore be searched) one page at a time, so a caller with no location
// gives us nothing to navigate by. Defaulting to page 1 in that case looks like a working jump
// that landed on the wrong place; leaving the viewer where it is is the honest no-op.
export const hasPageLocation = (loc) => loc != null && /\d/.test(String(loc));

// pdf.js renders a page's text layer asynchronously, after the canvas. Until it
// exists the page element is on-screen but holds no text nodes, so there is
// nothing for findSequentialNodes to match against.
const hasRenderedText = (element) => {
  if (!element) {
    return null;
  }

  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);

  while (walker.nextNode()) {
    if (walker.currentNode.textContent.trim()) {
      return element;
    }
  }

  return null;
};

// Ready when the quote has been FOUND on the page, not merely when the page has
// some text. pdf.js streams a text layer in over several hundred milliseconds, so
// matching the first time any text appears searched a half-rendered page — which
// is why highlighting used to work for quotes near the top of a page and fail for
// ones further down. Both matchers are read-only here; nothing is highlighted
// until the poll resolves.
//
// A page that mounts but never yields a match (image-only, or the quote genuinely
// is not there) is released after the grace period so the loading mask never
// stalls: the caller then highlights nothing, the same graceful no-op as before.
const waitingForQuoteOnPage = (page, text) => {
  let mountedAt = null;

  return () => {
    const pageEl = document.getElementById(`${PAGE_ID_PREFIX}${page}`);

    if (!pageEl) {
      return null;
    }

    if (mountedAt === null) {
      mountedAt = Date.now();
    }

    const nodes = findSequentialNodes(text, pageEl);

    if (nodes.length || locateTextRange(text, pageEl)) {
      return { pageEl, nodes };
    }

    const graceElapsed = Date.now() - mountedAt >= TEXT_LAYER_GRACE_MS;

    return graceElapsed && hasRenderedText(pageEl) ? { pageEl, nodes } : null;
  };
};

const useDocumentViewer = (documentName) => {
  const totalPages = useSelector(selectTotalPages);
  const paragraphRows = useSelector(selectParagraphRows);
  const documentParagraphs = useSelector(selectDocumentParagraphs);
  const ext = getFileExtension(documentName);

  const viewerRef = useRef();
  const pendingWaitsRef = useRef(new Set());
  const scrollSeqRef = useRef(0);
  const dispatch = useDispatch();

  const clearAllHighlightedNodes = () => {
    const spans = document.querySelectorAll(`.${classes.highlight_span}`);

    spans.forEach((el) => {
      el.classList.remove(classes.highlight_span);
      // A wrapper exists only to carry the highlight, so it has to come back out —
      // otherwise every jump leaves the text layer another span deeper.
      if (isHighlightWrapper(el)) {
        unwrapHighlight(el);
      }
    });
  };

  // Every in-flight wait registers its canceller here. A single shared timeout handle meant a
  // second waitForRender cleared the FIRST one's pending poll, and that first promise then never
  // settled — its caller sat awaiting it forever, holding the page mask up. Cancelling now always
  // settles the promise (with null) so the caller can unwind.
  const clearPendingWait = () => {
    pendingWaitsRef.current.forEach((cancel) => cancel());
    pendingWaitsRef.current.clear();
  };

  // Resolves with whatever `getTarget` returns once it is truthy, or null if it
  // never becomes ready within the timeout. Waiting on the DOM itself — rather
  // than on a fixed delay — is what keeps the highlight working when a large
  // document is slow to render.
  const waitForRender = (getTarget) =>
    new Promise((resolve) => {
      const deadline = Date.now() + RENDER_WAIT_TIMEOUT_MS;
      let handle;

      const cancel = () => {
        if (handle) {
          window.clearTimeout(handle);
          handle = undefined;
        }
        resolve(null);
      };

      const settle = (value) => {
        pendingWaitsRef.current.delete(cancel);
        resolve(value);
      };

      const poll = () => {
        const target = getTarget();

        if (target) {
          settle(target);
          return;
        }

        if (Date.now() >= deadline) {
          settle(null);
          return;
        }

        handle = window.setTimeout(poll, RENDER_POLL_INTERVAL_MS);
      };

      clearPendingWait();
      pendingWaitsRef.current.add(cancel);
      poll();
    });

  const scrollDocumentViewerToRow = async (rowIndex = 0) => {
    const ref = viewerRef?.current;
    if (ref) {
      await ref.scrollToCenter(Math.max(0, rowIndex));
    }
  };

  const scrollDocumentViewerByPage = (page = 1) =>
    scrollDocumentViewerToRow(page - 1);

  // A quote can run across a paragraph break, so each covered paragraph is highlighted with
  // just its own slice. Paragraphs the virtualizer has not mounted are skipped rather than
  // waited for; the one the quote starts on is the one that gets scrolled to.
  const highlightSegments = (segments) =>
    segments.flatMap((segment) => {
      const element = document.getElementById(
        `${DOX_ID_PREFIX}${segment.ordinal}`
      );

      return element
        ? highlightTextRange(segment.match, element, classes.highlight_span)
        : [];
    });

  // The quote is located in the document's text, not by paragraph index: the backend and
  // docx-preview number paragraphs differently, and a caller's quote is usually a sentence
  // inside a paragraph rather than the whole of one.
  const scrollDOCXByLocation = async ({
    paragraphDistance,
    text,
    showFullPageMask = true,
  }) => {
    // removeFromJobQueue filters EVERY entry with this key, so when two scrolls overlap the first
    // to finish tore down the mask the second was still relying on. The newest call owns the mask.
    const seq = scrollSeqRef.current + 1;
    scrollSeqRef.current = seq;

    if (showFullPageMask) {
      dispatch(addToJobQueue("scrollDOCXByLocation"));
    }

    try {
      clearAllHighlightedNodes();

      const anchor = resolveDocumentAnchor(documentParagraphs, {
        quote: text,
        hint: paragraphDistance,
      });

      if (!anchor) {
        return;
      }

      const { ordinal } = anchor.start;
      const hasSinglePage = totalPages <= 1;

      // A single-section document virtualizes by the article's child nodes, so the row holding
      // a paragraph has to be looked up; the paragraph's own index counts <p> elements and is a
      // different sequence. Multi-section documents virtualize by page, where row = page - 1.
      if (hasSinglePage) {
        const rowIndex = paragraphRows[ordinal];

        if (rowIndex != null) {
          await scrollDocumentViewerToRow(rowIndex);
        }
      } else if (anchor.pageNumber) {
        await scrollDocumentViewerByPage(anchor.pageNumber);
      }

      const para = await waitForRender(() =>
        document.getElementById(`${DOX_ID_PREFIX}${ordinal}`)
      );

      if (!para) {
        return;
      }

      const wrappers = highlightSegments(anchor.segments);

      if (wrappers.length) {
        wrappers[0].scrollIntoView(NODE_SCROLL_TO);
        return;
      }

      // The paragraph is on screen but its text would not line up character for character —
      // marking the whole paragraph still puts the reader in the right place.
      para.scrollIntoView(NODE_SCROLL_TO);
      para.classList.add(classes.highlight_span);
    } finally {
      if (showFullPageMask && seq === scrollSeqRef.current) {
        dispatch(removeFromJobQueue("scrollDOCXByLocation"));
      }
    }
  };

  const scrollPDFByLocation = async ({
    location = "",
    text,
    showFullPageMask = true,
  } = {}) => {
    if (!hasPageLocation(location)) {
      return;
    }

    // See scrollDOCXByLocation: the newest overlapping call owns the shared mask key.
    const seq = scrollSeqRef.current + 1;
    scrollSeqRef.current = seq;

    if (showFullPageMask) {
      dispatch(addToJobQueue("scrollPDFByLocation"));
    }

    try {
      const page = locationToPageNumber(location);
      await scrollDocumentViewerByPage(page);

      clearAllHighlightedNodes();

      const match = await waitForRender(waitingForQuoteOnPage(page, text));

      if (!match) {
        return;
      }

      const { pageEl, nodes } = match;

      if (nodes.length) {
        nodes.forEach((node) => {
          if (!node || node?.nodeType !== Node.TEXT_NODE) {
            return;
          }

          const parent = node?.parentElement;
          parent.classList.add(classes.highlight_span);
        });

        // Scrolling to the middle found element is good enough. Floor, not round:
        // a single matched node must land on index 0, not past the end.
        const middle = nodes[Math.floor(nodes.length / 2)];
        const parent = middle?.parentElement;
        if (parent) {
          parent.scrollIntoView(NODE_SCROLL_TO);
        }
        return;
      }

      // findSequentialNodes only matches a quote that starts and ends exactly on a
      // text-node boundary, which is why highlighting works on some paragraphs and
      // not others: a PDF text layer splits by rendered line, so most quoted
      // sentences begin part-way through a span. Fall back to a character-level
      // match that can wrap a partial node.
      const wrappers = highlightTextRange(text, pageEl, classes.highlight_span);

      if (!wrappers.length) {
        return;
      }

      wrappers[Math.floor(wrappers.length / 2)].scrollIntoView(NODE_SCROLL_TO);
    } finally {
      if (showFullPageMask && seq === scrollSeqRef.current) {
        dispatch(removeFromJobQueue("scrollPDFByLocation"));
      }
    }
  };

  // showFullPageMask blocks the whole page while the scroll settles — right for a
  // one-off jump through a large document (VT-7832), wrong for a caller that scrolls
  // repeatedly inside one pane, where it reads as the page flashing.
  const scrollViewerToLocation = ({
    location,
    paragraphDistance,
    text,
    showFullPageMask = true,
  }) => {
    switch (ext) {
      case "pdf":
        return scrollPDFByLocation({ location, text, showFullPageMask });
      case "docx":
        return scrollDOCXByLocation({
          paragraphDistance,
          text,
          showFullPageMask,
        });
      default:
        return false;
    }
  };

  useEffect(() => {
    return () => {
      clearPendingWait();
    };
  }, []);

  return {
    ext,
    scrollViewerToLocation,
    viewerRef,
  };
};

export default useDocumentViewer;
